import React from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { StyleSheet, View } from 'react-native';

import { Calories } from 'components/price-and-calories';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ProductPriceConditions, useProductPrice } from 'hooks/menu/use-product-price';
import { computeProductCalories } from 'hooks/menu/utils';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { primitive } from 'styles/constants/primitives';
import { Region } from 'utils/environment/types';

import { PriceAndCaloriesWrapper, PriceCaloriesText } from './styled';

interface IPriceAndCaloriesProps {
  productPriceConditions?: ProductPriceConditions;
  icon?: React.ReactNode;
}

export const PriceAndCalories: React.FC<IPriceAndCaloriesProps> = ({
  productPriceConditions,
  icon,
}) => {
  const { region } = useLocale();
  const { formattedProductPrice } = useProductPrice(productPriceConditions);

  const { selectedProduct } = useProductWizardContext();
  const { formatCalories } = useMenuCalories();

  const shouldComputeCalories = !!selectedProduct && !selectedProduct.hideCalories;
  const productCalories = shouldComputeCalories ? computeProductCalories(selectedProduct) : 0;
  const formattedProductCalories = formatCalories(productCalories);

  const priceAccessibilityLabel = t('totalPrice', { price: formattedProductPrice });

  const enableSodiumWarning = useFlag(LaunchDarklyFlag.ENABLE_SODIUM_WARNING);
  const shouldDisplaySodiumWarning = enableSodiumWarning && !!icon && region === Region.US;

  return (
    <Box alignItems="center">
      <PriceAndCaloriesWrapper>
        <PriceCaloriesText
          testID="product-total-price"
          accessibilityLabel={priceAccessibilityLabel}
          focusable
          accessible
        >
          {formattedProductPrice}
        </PriceCaloriesText>

        {!!formattedProductPrice &&
          !!formattedProductCalories &&
          !selectedProduct?.hideCalories && <PriceCaloriesText>{' | '}</PriceCaloriesText>}

        <Calories hideCalories={!!selectedProduct?.hideCalories}>
          <PriceCaloriesText
            testID="product-total-calories"
            focusable
            accessible
            accessibilityLabel={formattedProductCalories}
          >
            {formattedProductCalories}
          </PriceCaloriesText>
        </Calories>
      </PriceAndCaloriesWrapper>

      {shouldDisplaySodiumWarning && (
        <View style={styles.container}>
          {icon}
          <Text fontSize={{ base: primitive.fontSizes.lg, md: primitive.fontSizes['2xl'] }}>
            {t('sodiumWarning', 'Sodium Warning').toUpperCase()}
          </Text>
        </View>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 4,
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.$2,
  },
});
